import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import {
  UserPlus, FileDown, ShieldCheck, ShieldAlert, X,
  ChevronDown, ChevronUp, Trash2, Users2, MapPin, ArrowLeftRight,
} from "lucide-react";
import { Portal } from "@/components/Portal";

const AVATAR_PALETTE = [
  "#783D19", "#C45454", "#4F8A3A", "#3B6CA8", "#7C3AED", "#0F766E",
];

function initialsOf(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function colorFor(key) {
  if (!key) return AVATAR_PALETTE[0];
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

/**
 * Return the users eligible to hold an FSMS role at `siteId`.
 *
 * Eligibility:
 *  - Owners: always eligible (cross-site role).
 *  - Manager / supervisor / staff with empty/missing `site_ids`: chain-wide → eligible everywhere.
 *  - Otherwise: eligible only if `site_ids` includes the target site.
 *  - Archived users are always excluded.
 */
function eligibleForSite(users, siteId) {
  if (!siteId) return users.filter((u) => !u.is_archived);
  return users.filter((u) => {
    if (u.is_archived) return false;
    if (u.role === "owner") return true;
    const sids = u.site_ids || [];
    if (sids.length === 0) return true;       // chain-wide scope
    return sids.includes(siteId);
  });
}

function Avatar({ name, url, size = 36 }) {
  if (url) {
    return <img src={url} alt={name} className="rounded-full object-cover"
      style={{ width: size, height: size }} />;
  }
  return (
    <div className="rounded-full flex items-center justify-center font-bold text-white flex-shrink-0"
      style={{ width: size, height: size, background: colorFor(name), fontSize: size * 0.4 }}>
      {initialsOf(name)}
    </div>
  );
}

function AssignmentCard({ a, canEdit, onRevoke, onReassign }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="vb-card p-4" data-testid={`fsms-assignment-${a.id}`}>
      <div className="flex items-start gap-3">
        <Avatar name={a.user_name} url={a.user_avatar_url} size={42} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="font-semibold truncate" style={{ color: "var(--vb-text-2)" }}>
                {a.user_name}
              </div>
              <div className="text-xs opacity-70 truncate">{a.job_title || "—"}</div>
            </div>
            {canEdit && (
              <div className="flex flex-col gap-1 flex-shrink-0">
                <button className="vb-btn ghost text-sm" onClick={() => onRevoke(a)}
                  data-testid={`fsms-revoke-${a.id}`}>
                  <Trash2 size={12} /> Revoke
                </button>
                <button className="vb-btn ghost text-sm" onClick={() => onReassign(a)}
                  data-testid={`fsms-reassign-${a.id}`}>
                  <ArrowLeftRight size={12} /> Reassign
                </button>
              </div>
            )}
          </div>
          {a.qualifications && (
            <div className="text-xs mt-2"><strong>Qualifications:</strong> {a.qualifications}</div>
          )}
          <button className="text-xs mt-2 inline-flex items-center gap-1 opacity-70 hover:opacity-100"
            onClick={() => setExpanded(!expanded)}
            data-testid={`fsms-expand-${a.id}`}>
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {expanded ? "Hide responsibilities" : `${a.responsibilities?.length || 0} responsibilities`}
          </button>
          {expanded && (
            <ul className="list-disc pl-5 text-xs mt-2 space-y-1">
              {(a.responsibilities || []).map((r, i) => <li key={`${a.id}-${i}`}>{r}</li>)}
            </ul>
          )}
          <div className="text-[10px] opacity-50 mt-2">
            Appointed {(a.appointed_at || "").slice(0, 10)} by {a.appointed_by_name || "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

function AppointModal({ isOpen, onClose, siteId, siteName, users, roles, onCreated }) {
  const [userId, setUserId] = useState("");
  const [roleCode, setRoleCode] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [qualifications, setQualifications] = useState("");
  const [responsibilities, setResponsibilities] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Active assignments at THIS site — used to enforce the rule:
  // one person · one site · one FSMS role (the same user CAN hold a role
  // at multiple sites). Loaded on open so the dropdown only shows people
  // who don't already hold a different role here.
  const [siteAppts, setSiteAppts] = useState([]);

  const selectedRole = useMemo(() => roles.find((r) => r.code === roleCode), [roles, roleCode]);

  useEffect(() => {
    if (!isOpen) {
      setUserId(""); setRoleCode(""); setJobTitle(""); setQualifications("");
      setResponsibilities([]); setNotes(""); setErr(""); setSiteAppts([]);
      return;
    }
    if (!siteId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/fsms-team/site/${siteId}`);
        const flat = Object.values(data.grouped || {}).flat().filter((a) => a.is_active);
        if (!cancelled) setSiteAppts(flat);
      } catch { /* ignored — modal will fall back to backend rule */ }
    })();
    return () => { cancelled = true; };
  }, [isOpen, siteId]);

  // Apply the one-person-one-site rule on top of base eligibility.
  const eligibleUsers = useMemo(() => {
    const onSite = new Set(siteAppts.map((a) => a.user_id));
    return eligibleForSite(users, siteId).filter((u) => !onSite.has(u.id));
  }, [users, siteId, siteAppts]);

  useEffect(() => {
    if (selectedRole) setResponsibilities([...selectedRole.default_responsibilities]);
  }, [selectedRole]);

  const submit = async () => {
    if (!userId || !roleCode || !siteId) { setErr("Pick a user and role"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/fsms-team", {
        user_id: userId, site_id: siteId, fsms_role: roleCode,
        job_title: jobTitle, qualifications,
        responsibilities, appointment_notes: notes,
      });
      onCreated();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to appoint");
    } finally { setBusy(false); }
  };

  if (!isOpen) return null;

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(60, 40, 24, 0.6)" }}
      data-testid="fsms-appoint-modal">
      <div className="vb-card max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="vb-heading text-xl" style={{ color: "var(--vb-text-2)" }}>
            Appoint FSMS team member
          </h2>
          <button onClick={onClose} className="opacity-60 hover:opacity-100"
            data-testid="fsms-appoint-close"><X size={20} /></button>
        </div>
        {siteName && (
          <div className="text-xs opacity-70 mb-3">at <strong>{siteName}</strong></div>
        )}

        <div className="grid gap-3">
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wider"
              style={{ color: "var(--vb-text-2)" }}>Team member</label>
            <select className="vb-input" value={userId}
              onChange={(e) => setUserId(e.target.value)}
              data-testid="fsms-appoint-user">
              <option value="">— Select —</option>
              {eligibleUsers.map((u) => (
                <option key={u.id} value={u.id}>{u.name} · {u.email}</option>
              ))}
            </select>
            {eligibleUsers.length === 0 && (
              <div className="text-[11px] mt-1 opacity-70" data-testid="fsms-appoint-no-eligible">
                Everyone already holds an FSMS role at this site. Revoke an existing role first if you want to move someone.
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wider"
              style={{ color: "var(--vb-text-2)" }}>FSMS role</label>
            <select className="vb-input" value={roleCode}
              onChange={(e) => setRoleCode(e.target.value)}
              data-testid="fsms-appoint-role">
              <option value="">— Select —</option>
              {roles.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wider"
              style={{ color: "var(--vb-text-2)" }}>Job title (day job)</label>
            <input className="vb-input" value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="e.g. Head of Production"
              data-testid="fsms-appoint-job-title" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wider"
              style={{ color: "var(--vb-text-2)" }}>Qualifications</label>
            <textarea className="vb-input" rows={2} value={qualifications}
              onChange={(e) => setQualifications(e.target.value)}
              placeholder="e.g. 3+ years food production; attended FSMS Awareness training Mar 2026"
              data-testid="fsms-appoint-qualifications" />
          </div>
          {selectedRole && (
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wider"
                style={{ color: "var(--vb-text-2)" }}>Responsibilities (editable)</label>
              <div className="space-y-1">
                {responsibilities.map((r, i) => (
                  <div key={`r-${i}`} className="flex gap-2">
                    <textarea className="vb-input flex-1 text-xs" rows={1} value={r}
                      onChange={(e) => {
                        const copy = [...responsibilities]; copy[i] = e.target.value;
                        setResponsibilities(copy);
                      }} />
                    <button className="vb-btn ghost text-sm"
                      onClick={() => setResponsibilities(responsibilities.filter((_, idx) => idx !== i))}>
                      <X size={12} />
                    </button>
                  </div>
                ))}
                <button className="vb-btn ghost text-sm"
                  onClick={() => setResponsibilities([...responsibilities, ""])}
                  data-testid="fsms-appoint-add-responsibility">
                  + Add responsibility
                </button>
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wider"
              style={{ color: "var(--vb-text-2)" }}>Appointment notes</label>
            <textarea className="vb-input" rows={2} value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Replaces previous holder; effective immediately"
              data-testid="fsms-appoint-notes" />
          </div>
          {err && <div className="vb-pill err" data-testid="fsms-appoint-error">{err}</div>}
          <div className="flex gap-2 justify-end">
            <button className="vb-btn ghost text-sm" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="vb-btn text-sm" onClick={submit} disabled={busy}
              data-testid="fsms-appoint-submit">
              {busy ? "Saving…" : "Appoint"}
            </button>
          </div>
        </div>
      </div>
    </div>
    </Portal>
  );
}

/** Atomic swap modal: pick a new holder for an existing role + inherit
 *  the current job-title / qualifications / responsibilities as editable
 *  defaults. Fires `POST /fsms-team/{id}/reassign`. */
function ReassignModal({ isOpen, onClose, assignment, siteName, users, onReassigned }) {
  const [newUserId, setNewUserId] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [qualifications, setQualifications] = useState("");
  const [responsibilities, setResponsibilities] = useState([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (isOpen && assignment) {
      setNewUserId("");
      setJobTitle(assignment.job_title || "");
      setQualifications(assignment.qualifications || "");
      setResponsibilities([...(assignment.responsibilities || [])]);
      setReason("");
      setErr("");
    }
  }, [isOpen, assignment]);

  if (!isOpen || !assignment) return null;

  // Exclude the current holder AND filter by site scope.
  const candidates = eligibleForSite(users, assignment.site_id)
    .filter((u) => u.id !== assignment.user_id);

  const submit = async () => {
    if (!newUserId) { setErr("Pick a new team member."); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/fsms-team/${assignment.id}/reassign`, {
        new_user_id: newUserId,
        job_title: jobTitle,
        qualifications,
        responsibilities,
        reason,
      });
      onReassigned();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to reassign");
    } finally { setBusy(false); }
  };

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(60, 40, 24, 0.6)" }}
      data-testid="fsms-reassign-modal">
      <div className="vb-card max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="vb-heading text-xl" style={{ color: "var(--vb-text-2)" }}>
              Reassign role
            </h2>
            <div className="text-xs opacity-70 mt-0.5">
              {assignment.fsms_role_label}{siteName ? ` · ${siteName}` : ""}
            </div>
          </div>
          <button onClick={onClose} className="opacity-60 hover:opacity-100"
            data-testid="fsms-reassign-close"><X size={20} /></button>
        </div>

        <div className="vb-card p-3 mb-4" style={{ background: "var(--vb-bg-2)" }}>
          <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1">Current holder</div>
          <div className="flex items-center gap-2">
            <Avatar name={assignment.user_name} url={assignment.user_avatar_url} size={32} />
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate" style={{ color: "var(--vb-text-2)" }}>
                {assignment.user_name}
              </div>
              <div className="text-xs opacity-70 truncate">{assignment.job_title || "—"}</div>
            </div>
          </div>
        </div>

        <div className="grid gap-3">
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wider"
              style={{ color: "var(--vb-text-2)" }}>New team member</label>
            <select className="vb-input" value={newUserId}
              onChange={(e) => setNewUserId(e.target.value)}
              data-testid="fsms-reassign-user">
              <option value="">— Select replacement —</option>
              {candidates.map((u) => (
                <option key={u.id} value={u.id}>{u.name} · {u.email}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wider"
              style={{ color: "var(--vb-text-2)" }}>Job title (day job)</label>
            <input className="vb-input" value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              data-testid="fsms-reassign-job-title" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wider"
              style={{ color: "var(--vb-text-2)" }}>Qualifications</label>
            <textarea className="vb-input" rows={2} value={qualifications}
              onChange={(e) => setQualifications(e.target.value)}
              data-testid="fsms-reassign-qualifications" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wider"
              style={{ color: "var(--vb-text-2)" }}>Responsibilities (inherited — editable)</label>
            <div className="space-y-1">
              {responsibilities.map((r, i) => (
                <div key={`rr-${i}`} className="flex gap-2">
                  <textarea className="vb-input flex-1 text-xs" rows={1} value={r}
                    onChange={(e) => {
                      const copy = [...responsibilities]; copy[i] = e.target.value;
                      setResponsibilities(copy);
                    }} />
                  <button className="vb-btn ghost text-sm"
                    onClick={() => setResponsibilities(responsibilities.filter((_, idx) => idx !== i))}>
                    <X size={12} />
                  </button>
                </div>
              ))}
              <button className="vb-btn ghost text-sm"
                onClick={() => setResponsibilities([...responsibilities, ""])}
                data-testid="fsms-reassign-add-responsibility">
                + Add responsibility
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wider"
              style={{ color: "var(--vb-text-2)" }}>Reason (for audit log)</label>
            <input className="vb-input" value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Previous holder relocated to other site"
              data-testid="fsms-reassign-reason" />
          </div>
          {err && <div className="vb-pill err" data-testid="fsms-reassign-error">{err}</div>}
          <div className="flex gap-2 justify-end">
            <button className="vb-btn ghost text-sm" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="vb-btn text-sm" onClick={submit} disabled={busy}
              data-testid="fsms-reassign-submit">
              {busy ? "Saving…" : "Reassign"}
            </button>
          </div>
        </div>
      </div>
    </div>
    </Portal>
  );
}

function RevokeModal({ isOpen, assignment, siteName, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (isOpen) { setReason(""); setErr(""); setBusy(false); }
  }, [isOpen, assignment]);

  if (!isOpen || !assignment) return null;

  const confirm = async () => {
    setBusy(true); setErr("");
    try {
      await onConfirm(reason);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to revoke");
    } finally { setBusy(false); }
  };

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(60, 40, 24, 0.6)" }}
      data-testid="fsms-revoke-modal">
      <div className="vb-card max-w-md w-full p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="vb-heading text-xl" style={{ color: "var(--vb-text-2)" }}>Revoke role</h2>
          <button onClick={onClose} className="opacity-60 hover:opacity-100"
            data-testid="fsms-revoke-close"><X size={20} /></button>
        </div>
        <p className="text-sm mb-4" style={{ color: "var(--vb-text-2)" }}>
          Remove <strong>{assignment.user_name}</strong> from <strong>{assignment.fsms_role_label}</strong>
          {siteName ? <> at <strong>{siteName}</strong></> : null}? This reopens the vacancy.
        </p>
        <label className="text-[10px] uppercase tracking-wider opacity-70">Reason (optional, for the audit log)</label>
        <textarea className="vb-input mt-1 w-full" rows={3} value={reason}
          onChange={(e) => setReason(e.target.value)} placeholder="e.g. Staff member left the company"
          data-testid="fsms-revoke-reason" />
        {err && <div className="vb-pill err mt-3">{err}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button className="vb-btn ghost" onClick={onClose} disabled={busy}
            data-testid="fsms-revoke-cancel">Cancel</button>
          <button className="vb-btn danger" onClick={confirm} disabled={busy}
            data-testid="fsms-revoke-confirm">
            <Trash2 size={14} /> {busy ? "Revoking…" : "Revoke"}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}


/** Renders one site's FSMS team — used for both single-site and per-column views. */
function SiteFSMSPanel({
  site, roles, users, canEdit, status, onAppoint, onReassign, onReload, onError,
  compact = false,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revokeTarget, setRevokeTarget] = useState(null);

  const load = useCallback(async () => {
    if (!site?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/fsms-team/site/${site.id}`);
      setData(res.data);
    } catch (e) {
      console.error("FSMS site load failed:", e);
      onError?.("Failed to load FSMS data for " + site.name);
    } finally { setLoading(false); }
  }, [site?.id, site?.name, onError]);

  useEffect(() => { load(); }, [load]);

  // Expose reload to parent
  useEffect(() => {
    if (onReload) onReload(site.id, load);
  }, [site.id, load, onReload]);

  const doRevoke = async (reason) => {
    await api.post(`/fsms-team/${revokeTarget.id}/revoke`, { reason });
    load();
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/fsms-team/pdf/${site.id}`, { responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : "fsms-team.pdf";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("PDF download failed:", e);
      onError?.("PDF generation failed");
    }
  };

  // Freeze the current site team into the Audit Vault. Server returns a
  // 412 with `code: signature_required` if the user hasn't uploaded
  // their signature yet — we surface that as a clickable banner via
  // onError so the user can go to Profile.
  const generateForAudit = async () => {
    try {
      const { data: res } = await api.post("/audit-vault/generate", {
        report_type: "fsms_team",
        site_id: site.id,
      });
      window.location.href = `/app/audit-vault?focus=${encodeURIComponent(res.report?.id || "")}`;
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det && typeof det === "object") {
        onError?.(det.message || "Couldn't file for audit");
      } else {
        onError?.(det || "Couldn't file for audit");
      }
    }
  };

  return (
    <div className="vb-card p-4 flex flex-col" data-testid={`fsms-site-panel-${site.id}`}>
      {/* Column header */}
      <div className="flex items-start justify-between gap-2 mb-3 pb-3"
        style={{ borderBottom: "1px solid var(--vb-border)" }}>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <MapPin size={14} style={{ color: "var(--vb-text-2)" }} />
            <h3 className="vb-heading text-base truncate" style={{ color: "var(--vb-text-2)" }}>
              {site.name}
            </h3>
          </div>
          <div className="mt-1.5">
            {status?.compliant ? (
              <span className="vb-pill ok inline-flex items-center gap-1 text-[10px]"
                data-testid={`fsms-site-compliant-${site.id}`}>
                <ShieldCheck size={10} /> Compliant
              </span>
            ) : (
              <span className="vb-pill err inline-flex items-center gap-1 text-[10px]"
                data-testid={`fsms-site-noncompliant-${site.id}`}>
                <ShieldAlert size={10} /> {status?.vacancies?.length || 0} vacancy(ies)
                {(() => {
                  const maxAge = Math.max(
                    0,
                    ...(status?.vacancies || []).map((v) => v.age_days || 0),
                  );
                  if (maxAge >= 7) return <span className="ml-1 opacity-90">· {maxAge}d</span>;
                  return null;
                })()}
              </span>
            )}
          </div>
        </div>
        {canEdit && (
          <div className="flex flex-col gap-1.5 flex-shrink-0">
            <button className="vb-btn text-sm" onClick={() => onAppoint(site)}
              data-testid={`fsms-appoint-btn-${site.id}`}>
              <UserPlus size={12} /> Appoint
            </button>
            <button className="vb-btn ghost text-sm" onClick={downloadPdf}
              data-testid={`fsms-download-pdf-${site.id}`}>
              <FileDown size={12} /> PDF
            </button>
            <button className="vb-btn ghost text-sm" onClick={generateForAudit}
              data-testid={`fsms-generate-audit-${site.id}`}
              title="Freeze this team snapshot and start the dual-signature workflow in the Audit Vault.">
              <ShieldCheck size={12} /> File for audit
            </button>
          </div>
        )}
      </div>

      {/* Roles & assignments */}
      <div className="flex-1 space-y-4">
        {loading && <div className="text-xs opacity-60 text-center py-6">Loading…</div>}
        {!loading && data && roles.map((role) => {
          const members = (data.grouped[role.code] || []);
          const isVacant = members.length < role.min_count;
          const vacInfo = (status?.vacancies || []).find((v) => v.role_code === role.code);
          const ageDays = vacInfo?.age_days;
          const urgency = vacInfo?.urgency;
          return (
            <section key={role.code} data-testid={`fsms-role-${site.id}-${role.code}`}>
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <Users2 size={14} style={{ color: "var(--vb-text-2)" }} />
                <h4 className="vb-heading text-sm" style={{ color: "var(--vb-text-2)" }}>
                  {role.label}
                </h4>
                <span className="text-[10px] opacity-60">
                  {members.length} / {role.max_count ? role.max_count : "many"}
                </span>
                {isVacant && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                    style={{
                      background:
                        urgency === "red" ? "#7F1D1D"
                          : urgency === "amber" ? "#B45309"
                          : "#783D19",
                      color: "#fff",
                    }}
                    data-testid={`fsms-vacancy-pill-${site.id}-${role.code}`}
                    title={ageDays != null ? `Vacant for ${ageDays} day(s)` : "Vacant"}
                  >
                    Vacant
                    {ageDays != null && ageDays >= 1 && <> · {ageDays}d</>}
                  </span>
                )}
              </div>
              {members.length === 0 ? (
                <div className="text-xs opacity-50 italic pl-1">Not yet appointed.</div>
              ) : (
                <div className={compact ? "grid gap-2" : "grid md:grid-cols-2 gap-2"}>
                  {members.map((a) => (
                    <AssignmentCard key={a.id} a={a} canEdit={canEdit}
                      onRevoke={setRevokeTarget}
                      onReassign={(asg) => onReassign?.(asg, site)} />
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>
      <RevokeModal
        isOpen={!!revokeTarget}
        assignment={revokeTarget}
        siteName={site?.name}
        onClose={() => setRevokeTarget(null)}
        onConfirm={doRevoke}
      />
    </div>
  );
}

export default function FSMSTeam() {
  const { visibleSites, activeSiteId, isAllSites, loaded: sitesLoaded } = useSite();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [status, setStatus] = useState([]);
  const [me, setMe] = useState({ role: "", id: "" });
  const [appointSite, setAppointSite] = useState(null); // null = closed
  const [reassign, setReassign] = useState(null); // {assignment, site} | null
  const [err, setErr] = useState("");
  const [reloaders, setReloaders] = useState({}); // siteId -> load fn

  const canEdit = ["owner", "manager"].includes(me.role);

  const loadInit = useCallback(async () => {
    try {
      const [meRes, usersRes, rolesRes, statusRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/users"),
        api.get("/fsms-team/roles"),
        api.get("/fsms-team/status"),
      ]);
      const meUser = meRes.data.user || meRes.data;
      setMe({ role: meUser.role || "", id: meUser.id || "" });
      setUsers((usersRes.data.users || []).filter((u) => !u.is_archived));
      setRoles(rolesRes.data.roles || []);
      setStatus(statusRes.data.sites || []);
    } catch (e) {
      console.error("FSMS init load failed:", e);
      setErr("Failed to load page. Please refresh.");
    }
  }, []);

  useEffect(() => { loadInit(); }, [loadInit]);

  const refreshStatus = useCallback(async () => {
    try {
      const res = await api.get("/fsms-team/status");
      setStatus(res.data.sites || []);
    } catch (e) {
      console.error("FSMS status refresh failed:", e);
    }
  }, []);

  const statusBySite = useMemo(
    () => Object.fromEntries(status.map((s) => [s.site_id, s])),
    [status],
  );

  // Sites to render: all if "All Sites", else just the active one.
  const sitesToRender = useMemo(() => {
    if (isAllSites) return visibleSites;
    const one = visibleSites.find((s) => s.id === activeSiteId);
    return one ? [one] : [];
  }, [isAllSites, visibleSites, activeSiteId]);

  const registerReload = useCallback((siteId, fn) => {
    setReloaders((prev) => (prev[siteId] === fn ? prev : { ...prev, [siteId]: fn }));
  }, []);

  const onAppointed = () => {
    const siteId = appointSite?.id;
    if (siteId && reloaders[siteId]) reloaders[siteId]();
    refreshStatus();
  };

  const onReassigned = () => {
    const siteId = reassign?.site?.id;
    if (siteId && reloaders[siteId]) reloaders[siteId]();
    refreshStatus();
  };

  // Layout: when >1 sites shown, horizontal scrollable columns; else single full-width.
  const multi = sitesToRender.length > 1;

  return (
    <div className="vb-fade-in" data-testid="fsms-team-page">
      <h1 className="vb-heading text-3xl mb-1" style={{ color: "var(--vb-text-2)" }}>
        FSMS Team
      </h1>
      <p className="opacity-70 mb-6 max-w-3xl">
        Appoint team members to ISO 22000 Food Safety Management committee roles per site.
        Every site needs at least one Management Representative, one Team Leader,
        and one or more Team Members. Use the site filter in the header to switch between sites
        or view all sites side-by-side.
      </p>

      {err && (
        <div className="vb-pill err mb-4 inline-flex items-center gap-2" data-testid="fsms-error">
          {err}
          <button onClick={() => setErr("")} className="opacity-70 hover:opacity-100">
            <X size={12} />
          </button>
        </div>
      )}

      {!sitesLoaded && (
        <div className="text-sm opacity-60">Loading sites…</div>
      )}

      {sitesLoaded && sitesToRender.length === 0 && (
        <div className="vb-card p-8 text-center opacity-70">
          No sites available. Ask your owner/admin to create a site first.
        </div>
      )}

      {sitesToRender.length > 0 && (
        <div
          className={
            multi
              ? "grid gap-4 overflow-x-auto"
              : "grid gap-4"
          }
          style={
            multi
              ? {
                  gridTemplateColumns: `repeat(${sitesToRender.length}, minmax(340px, 1fr))`,
                }
              : undefined
          }
          data-testid="fsms-sites-grid"
        >
          {sitesToRender.map((s) => (
            <SiteFSMSPanel
              key={s.id}
              site={s}
              roles={roles}
              users={users}
              canEdit={canEdit}
              status={statusBySite[s.id]}
              onAppoint={(site) => setAppointSite(site)}
              onReassign={(asg, site) => setReassign({ assignment: asg, site })}
              onReload={registerReload}
              onError={setErr}
              compact={multi}
            />
          ))}
        </div>
      )}

      <AppointModal
        isOpen={!!appointSite}
        onClose={() => setAppointSite(null)}
        siteId={appointSite?.id}
        siteName={appointSite?.name}
        users={users}
        roles={roles}
        onCreated={onAppointed}
      />

      <ReassignModal
        isOpen={!!reassign}
        onClose={() => setReassign(null)}
        assignment={reassign?.assignment}
        siteName={reassign?.site?.name}
        users={users}
        onReassigned={onReassigned}
      />
    </div>
  );
}
